import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTutorial } from '../../context/TutorialContext';

// Gap kept between the tooltip and the screen edge when it has to be pulled back in
const VIEWPORT_MARGIN = 12;

// Функция вычисления позиции в зависимости от класса
const calculatePosition = (className, anchorRect) => {
  const { top, left, width, height } = anchorRect;

  // getBoundingClientRect() returns viewport-relative coordinates.
  // The tooltip uses position:fixed which is also viewport-relative,
  // so scrollX/scrollY must NOT be added here.
  switch (className) {
    case 'f-top':
      return {
        top: top + height + 30,
        left: left + width / 2,
        transform: 'translateX(-50%)',
      };
    case 'f-bottom':
      return {
        top: top - 30,
        left: left + width / 2,
        transform: 'translateX(-50%)',
      };
    case 'f-left':
      return {
        top: top + height / 2,
        left: left + width + 20,
        transform: 'translateY(-50%)',
      };
    case 'f-right':
      return {
        top: top + height / 2,
        left: left - 20,
        transform: 'translate(-100%, -50%)',
      };
    default:
      // Default placement is below the anchor
      return {
        top: top + height + 10,
        left: left,
        transform: 'none',
      };
  }
};

// Fallback position for tips whose anchor is not measured yet: the middle of the
// screen. The clamping pass below still checks and corrects it.
const getFallbackPosition = () => ({
  top: Math.round((window.innerHeight || 0) / 2),
  left: Math.round((window.innerWidth || 0) / 2),
  transform: 'translate(-50%, -50%)',
});

const toCssLength = (value) => (typeof value === 'number' ? `${value}px` : value);

const isInsideViewport = (rect) => {
  const viewportWidth = window.innerWidth || 0;
  const viewportHeight = window.innerHeight || 0;
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewportHeight &&
    rect.right <= viewportWidth
  );
};

// Drops the forced values from the previous pass so the tooltip can be measured
// in its normal position.
const clearViewportRescue = (element, position) => {
  element.style.removeProperty('right');
  element.style.removeProperty('bottom');
  element.style.removeProperty('margin');
  element.style.setProperty('top', toCssLength(position.top));
  element.style.setProperty('left', toCssLength(position.left));
  element.style.setProperty('transform', position.transform || 'none');
};

// Pulls the whole tooltip back into the viewport. Values are set as important
// because some classes (.f-bottom) also set their coordinates with important.
const applyViewportRescue = (element, rect) => {
  const viewportWidth = window.innerWidth || 0;
  const viewportHeight = window.innerHeight || 0;
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - rect.width);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - rect.height);
  const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), maxLeft);
  const top = Math.min(Math.max(rect.top, VIEWPORT_MARGIN), maxTop);

  element.style.setProperty('top', `${top}px`, 'important');
  element.style.setProperty('left', `${left}px`, 'important');
  element.style.setProperty('right', 'auto', 'important');
  element.style.setProperty('bottom', 'auto', 'important');
  element.style.setProperty('transform', 'none', 'important');
  element.style.setProperty('margin', '0', 'important');
};

const TooltipPortal = () => {
  const { activeTips, dismissTip } = useTutorial();
  const [positions, setPositions] = useState({});
  const tooltipElements = useRef({});

  // Функция обновления всех позиций
  const updatePositions = useCallback(() => {
    const newPositions = {};
    activeTips.forEach(tip => {
      const anchor = tip.ref?.current;
      newPositions[tip.id] = anchor
        ? calculatePosition(tip.className, anchor.getBoundingClientRect())
        : getFallbackPosition();
    });
    setPositions(newPositions);
  }, [activeTips]);

  // Обновляем позиции при изменении списка подсказок, скролле или ресайзе
  useEffect(() => {
    updatePositions();
    window.addEventListener('scroll', updatePositions);
    window.addEventListener('resize', updatePositions);
    return () => {
      window.removeEventListener('scroll', updatePositions);
      window.removeEventListener('resize', updatePositions);
    };
  }, [updatePositions]);

  // Second pass: measure the rendered tooltip and, if it landed off screen, move
  // it back inside together with its close button.
  useLayoutEffect(() => {
    activeTips.forEach(tip => {
      const element = tooltipElements.current[tip.id];
      if (!element) {
        return;
      }

      clearViewportRescue(element, positions[tip.id] || getFallbackPosition());
      const rect = element.getBoundingClientRect();
      // A zero rect means there is no layout information (test environment or a
      // hidden element). Leave the tooltip alone, never hide it.
      if (!rect.width && !rect.height) {
        return;
      }
      if (isInsideViewport(rect)) {
        return;
      }
      applyViewportRescue(element, rect);
    });
  }, [activeTips, positions]);

  // Если нет активных подсказок, ничего не рендерим
  if (activeTips.length === 0) return null;

  const portalRoot = document.getElementById('tooltip-portal');
  if (!portalRoot) return null;

  // Every active tip always gets its own box with a close button.
  const renderedTips = activeTips.map(tip => ({
    tip,
    position: positions[tip.id] || getFallbackPosition(),
  }));

  return ReactDOM.createPortal(
    <>
      {renderedTips.length > 0 ? (
        <div
          className="tutorial-overlay"
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {/* Сами подсказки */}
      {renderedTips.map(({ tip, position }) => (
        <div
          key={tip.id}
          ref={(element) => {
            if (element) {
              tooltipElements.current[tip.id] = element;
            } else {
              delete tooltipElements.current[tip.id];
            }
          }}
          className={`tutorial-tooltip ${tip.className || ''}`}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: position.transform,
            zIndex: 1001,
            pointerEvents: 'auto',
          }}
        >
          <button
            onClick={() => dismissTip(tip.id)}
            className="tutorial-tooltip-close icon-r-b-cross"
          />
          <div className="tutorial-tooltip-header">
            <h3>{tip.title}</h3>
          </div>
          <div className="tutorial-tooltip-content">
            <p>{tip.description}</p>
          </div>
        </div>
      ))}
    </>,
    portalRoot
  );
};

export default TooltipPortal;
